import { FC, useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import createSchema from "./CreateForm.schema"
import addItemSchema from "./AddItem.schema"
import Modal from "Components/UI/Modal";
import Form from "Components/UI/Form/Form";
import Field from "Components/UI/Field";
import Button from "Components/UI/Button";
import { AdmActions } from "Store/Adm/Adm.actions";
import { IGlobalReducerState } from "Store/Base/interface/IGlobalReducerState";
import SelectForm, { IOptionData } from "Components/UI/SelectForm";
import {
  ConstructionSiteDisciplineLibraryEnum,
  IConstructionSiteDisciplineLibrary,
  ICreateDisciplineLibraryRequest,
  IDisciplineLibraryItem,
  IUpdateDisciplineLibraryRequest,
} from "Data/interfaces/Obra/IDisciplineLibraries";
import toastHandler from 'Utils/toastHandler';
import { v4 as uuid } from "uuid";
import { Icon, iconClasses, IconName } from "Components/UI";
import { isObjectEmpty } from "Utils/ObjectUtils";
import IconPicker from "Components/UI/IconPicker";
import DisciplineIconPicker from "Pages/EditObra/components/Disciplines/components/DisciplineIconPicker";
import {
  Card,
  Content,
  FormContainer,
  FormWrapper,
  HeaderContent,
  Main,
} from "./styles";

interface InputLibProps {
  Name: string;
  Description: string;
}

interface InputLibItemProps {
  Acronym: string;
  DisciplineName: string;
}

interface ICreateForm {
  visible?: boolean;
  editLib: IConstructionSiteDisciplineLibrary | null;
  onClose: () => void;
}

const headerDisciplinesTable = [
  {
    id: '1',
    label: '',
  },
  {
    id: '2',
    label: 'Sigla',
  },
  {
    id: '3',
    label: 'Disciplina',
  },
];

const CreateForm: FC<Props> = ({
  editLib,
  visible,
  isLoading,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [isSubmit, setIsSubmit] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [type, setType] = useState<ConstructionSiteDisciplineLibraryEnum | null>(
    null
  );
  const [dataItems, setDataItems] = useState<Record<string, IDisciplineLibraryItem>>({});
  const [libName, setLibName] = useState('');
  const [libDescription, setLibDescription] = useState('');
  const [acronym, setAcronym] = useState('');
  const [disciplineName, setDisciplineName] = useState('');
  const [itemIcon, setItemIcon] = useState<IconName>('menuContexto');
  const [iconName, setIconName] = useState<IconName>(editLib?.IconName || 'coordly');

  const items = useMemo(() => {
    if (isObjectEmpty(dataItems)) return [];

    return Object.values(dataItems);
  }, [dataItems]);

  const submit = (values: InputLibProps) => {
    if (values && type !== null) {
      setIsSending(true);

      const request: ICreateDisciplineLibraryRequest = {
        name: values.Name,
        description: values.Description,
        type: type,
        iconName: iconName,
        items,
      }
      if (!!editLib) {
        const upRequest: IUpdateDisciplineLibraryRequest = {
          disciplineLibraryId: editLib.ConstructionSiteDisciplineLibraryId,
          disciplineLibrary: request,
        }
        dispatch(AdmActions.updateDisciplineLibrary(upRequest));
      } else {
        dispatch(AdmActions.createDisciplineLibrary(request));
      }
    }

    setIsSubmit(false);
  }

  const addItem = (values: InputLibItemProps) => {
    if (
      items.some(item => {
        return item?.acronym?.toLowerCase() === values?.Acronym?.toLowerCase() ||
          item?.name?.toLowerCase() === values?.DisciplineName?.toLowerCase();
      })
    ) {
      toastHandler.handler({ description: 'Sigla ou Nome já existente', icon: 'error', title: '' });
      setAcronym('');
      setDisciplineName('');
      return;
    }

    if (values) {
      const id = uuid();

      setDataItems(prev => {
        prev[id] = {
          name: values.DisciplineName,
          acronym: values.Acronym,
          icon: itemIcon,
        };

        return { ...prev };
      })
      setItemIcon('menuContexto');
    }
  }

  const removeItem = (id: string) => {
    setDataItems(prev => {
      if (prev.hasOwnProperty(id)) {
        delete prev[id];
      }

      return { ...prev };
    })
  }

  const onCloseAux = () => {
    onClose();
  }

  const iconOptions = useMemo(() => {
    return Object.keys(iconClasses) as IconName[];
  }, []);

  const optionsSelect = [
    { value: 0, description: 'Padrão Coordly' },
    { value: 1, description: 'Padrão Mercado' },
  ] as IOptionData[];

  const btnIsDisabled = isLoading ||
    type === null ||
    items.length === 0;

  const buttons = (
    <div>
      <Button
        onClick={onCloseAux}
      >
        <span>Cancelar</span>
      </Button>
      <Button
        type="primary"
        htmlType="submit"
        loading={isLoading}
        disabled={btnIsDisabled}
        onClick={() => setIsSubmit(true)}
      >
        <span>Confirmar</span>
      </Button>
    </div>
  );

  useEffect(() => {
    if (!!editLib) {
      setLibName(editLib.Name);
      setLibDescription(editLib?.Description || '');
      setType(editLib.Type);
      setIconName(editLib?.IconName || 'coordly');
      const newDataItems: Record<string, IDisciplineLibraryItem> = {};
      editLib.Items.forEach(item => {
        const id = uuid();
        newDataItems[id] = {
          name: item.Name,
          acronym: item.Acronym,
          icon: item.Icon || 'menuContexto',
        }
      });
      setDataItems(newDataItems);
    }
  }, [editLib]);

  useEffect(() => {
    if (isSending && !isLoading) {
      setIsSending(false);
      onCloseAux();
    }
  }, [isSending, isLoading]);

  return (
    <Modal
      title={!!editLib ? 'Editar Biblioteca' : 'Criar Nova Biblioteca'}
      visible={visible}
      footerButtons={buttons}
      onCancel={onCloseAux}
    >
      <FormWrapper>
        <FormContainer>
          <Form
            schema={createSchema}
            onSubmit={submit}
            isSubmited={isSubmit}
            resetForm={true}
            initialValues={{
              Name: editLib?.Name || '',
              Description: editLib?.Description || '',
            }}
          >
            <div className="rowName">
              <Field
                label="Nome Biblioteca"
                name="Name"
                value={libName}
                onCustomChange={setLibName}
              />
              <SelectForm
                onSelect={(value: any) => setType(value)}
                className="selectRole"
                placeholder="Tipo Biblioteca"
                options={optionsSelect}
                name="Type"
                defaultValue={type}
                style={{ width: 160 }}
              />
            </div>
            <div className="rowIcon">
              <IconPicker
                options={iconOptions}
                value={iconName}
                onChange={setIconName}
              />
              {iconName && (
                <div className="iconContainer">
                  <Icon
                    icon={iconName}
                    customSize={18}
                    className="icon"
                  />
                </div>
              )}
            </div>
            <Field
              label="Descrição Biblioteca"
              name="Description"
              value={libDescription}
              onCustomChange={setLibDescription}
            />
          </Form>

          <Form
            schema={addItemSchema}
            onSubmit={addItem}
            resetForm={true}
            initialValues={{ Acronym: '', DisciplineName: '' }}
          >
            <Content>
              <span className="itemIconPicker">
                <DisciplineIconPicker
                  value={itemIcon}
                  onChange={setItemIcon}
                />
              </span>
              <span className="containerField">
                <Field
                  name="Acronym"
                  label="Sigla"
                  className="fieldAcronym"
                  maxLenght={5}
                  value={acronym}
                  onCustomChange={(value: string) => setAcronym(value.toUpperCase())}
                />
              </span>
              <span className="containerField">
                <Field
                  name="DisciplineName"
                  label="Nome da Disciplina"
                  className="fieldStageName"
                  value={disciplineName}
                  onCustomChange={setDisciplineName}
                />
              </span>
              <Button
                type="primary"
                htmlType="submit"
                className="addButton"
                disabled={isLoading || !acronym || !disciplineName}
              >
                <Icon
                  icon="adicionar"
                  customSize={16}
                  className="addIconButton"
                />
              </Button>
            </Content>
          </Form>

          <HeaderContent>
            {headerDisciplinesTable.map(head => (
              <div key={`h-p-t-${head.id}`} className={`column${head.id}`}>
                <span className="columnText">{head.label}</span>
              </div>
            ))}
          </HeaderContent>
          <Main>
            {!isObjectEmpty(dataItems) && (
              <>
                {Object.entries(dataItems).map(([id, item]) => (
                  <Card key={id}>
                    <span className="row0">
                      <Icon icon={item.icon || 'menuContexto'} customSize={16} className="cardIcon" />
                    </span>
                    <span className="row1">{item.acronym}</span>
                    <span className="row2">
                      <span>{item.name}</span>
                      <Button
                        type="text"
                        htmlType="submit"
                        onClick={() => removeItem(id)}
                        className="removeButton"
                      >
                        <Icon
                          icon="excluir"
                          customSize={14}
                          className="removeIconButton"
                        />
                      </Button>
                    </span>
                  </Card>
                ))}
              </>
            )}
          </Main>
        </FormContainer>
      </FormWrapper>
    </Modal>
  )
}

const mapState = (state: IGlobalReducerState) => ({
  ...state.adm
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & ICreateForm;

export default connector(CreateForm);
